/** Sentinel value for agents that are not members of any queue. */
export const NOT_IN_QUEUE = 'not-in-queue' as const;

/** Status filter metadata used when matching agent presence. */
type StatusFilterEntry = {
  status: string;
  sub_type: string;
};

/** Agent presence fields used by status matching. */
type AgentPresence = {
  status?: string;
  subType?: string;
};

/**
 * Live panel filter object.
 * - `status: undefined` = all statuses (default).
 * - `status: [...]` = specific status ids.
 * - `status` omitted = status dimension inactive.
 * - `queues` includes queue numbers and/or {@link NOT_IN_QUEUE}.
 */
export type LivePanelFilter = {
  status?: Array<string | number>;
  queues?: Array<string | number>;
};

/** Arguments for {@link matchesAllFilters}. */
type MatchesAllFiltersParams = {
  filter: LivePanelFilter;
  statusFilterList: StatusFilterEntry[];
  agentStatus: string;
  presence: AgentPresence;
  agentQueueNumbers: Array<string | number>;
  groupingBy: string;
};

/**
 * Check whether an agent presence matches a status filter entry.
 *
 * @param filter - Status filter metadata (`status`, `sub_type`).
 * @param agentStatus - Agent extension status.
 * @param presence - Agent presence (`status`, `subType`).
 * @returns Whether the agent matches this status entry.
 */
const checkStatusFilterType = (
  filter: StatusFilterEntry,
  agentStatus: string,
  presence: AgentPresence = {},
): boolean => {
  const status = filter.status.toLowerCase();
  const presenceStatus = presence.status?.toLowerCase();
  const subType = presence.subType?.toLowerCase();
  const filterSubType = filter.sub_type.toLowerCase();

  switch (status) {
    case 'unavailable':
      return agentStatus === 'unavailable';
    case 'available':
    case 'xa':
    case 'chat':
    case 'dnd':
      return agentStatus !== 'unavailable' && presenceStatus === status;
    case 'away':
      return agentStatus !== 'unavailable' && presenceStatus === status && subType === filterSubType;
    default:
      return false;
  }
};

/**
 * Match an agent against the status filter list (OR within the list).
 * When status is inactive or `undefined` (all statuses), every agent passes.
 *
 * @param statusActive - Whether a concrete status id list should be applied.
 * @param statusFilters - Resolved status metadata for selected ids.
 * @param agentStatus - Agent extension status.
 * @param presence - Agent presence.
 * @returns Whether the agent passes the status dimension.
 */
const matchesStatusFilter = (
  statusActive: boolean,
  statusFilters: StatusFilterEntry[],
  agentStatus: string,
  presence: AgentPresence,
): boolean => {
  if (!statusActive) {
    return true;
  }
  if (!statusFilters?.length) {
    return false;
  }
  return statusFilters.some((filter) => checkStatusFilterType(filter, agentStatus, presence));
};

/**
 * Match an agent against the queues filter list (OR within the list).
 * Supports the {@link NOT_IN_QUEUE} sentinel.
 *
 * @param queueFilters - Queue numbers and/or `'not-in-queue'`.
 * @param agentQueueNumbers - Queue numbers the agent belongs to.
 * @param groupingBy - Current live panel grouping mode.
 * @returns Whether the agent passes the queues dimension.
 */
const matchesQueuesFilter = (
  queueFilters: Array<string | number> | undefined,
  agentQueueNumbers: Array<string | number>,
  groupingBy: string,
): boolean => {
  if (!queueFilters?.length) {
    return true;
  }

  return queueFilters.some((entry) => {
    if (entry === NOT_IN_QUEUE) {
      return !agentQueueNumbers.length;
    }
    // When grouped by queues, membership is implied by the panel
    if (groupingBy === 'queues') {
      return true;
    }
    return agentQueueNumbers.includes(entry);
  });
};

/**
 * AND across status and queues dimensions; OR within each list.
 * `status: undefined` means all statuses (status dimension passes).
 *
 * @param params - Filter context and agent state.
 * @returns Whether the agent matches all active filter dimensions.
 */
export const matchesAllFilters = ({
  filter,
  statusFilterList,
  agentStatus,
  presence,
  agentQueueNumbers,
  groupingBy,
}: MatchesAllFiltersParams): boolean => {
  const applyStatusList = Array.isArray(filter.status) && filter.status.length > 0;
  const statusOk = matchesStatusFilter(applyStatusList, statusFilterList, agentStatus, presence);
  const queuesOk = matchesQueuesFilter(filter.queues, agentQueueNumbers, groupingBy);
  return statusOk && queuesOk;
};

/**
 * Whether any filter dimension is active.
 *
 * @param filter - Live panel filter object.
 * @returns `true` if `status` key is present (including `undefined` = all) or queues has entries.
 */
export const hasActiveFilter = (filter?: { status?: unknown; queues?: unknown[] }): boolean =>
  !!(filter && ('status' in filter || filter.queues?.length));
